import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Info } from 'lucide-react';
import { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ReferenceLine } from 'recharts';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import { getQuadrant, getQuadrantFillClass, getQuadrantStrokeClass, getQuadrantTextClass, Quadrant } from './riskDynamicsHelpers';
import ScatterChartTooltip from './ScatterChartTooltip';

export type RiskDynamicsSite = {
    id: string;
    name: string;
    dependencies: number;
    impactOnNature: number;
    physicalRisk: number | null;
    stateOfNature: number | null;
};

type Props = {
    sites: RiskDynamicsSite[];
    highlightedSiteId?: string;
};

type ScatterPoint = RiskDynamicsSite & {
    x: number;
    y: number;
    fillClass: string;
    strokeClass: string;
    highlighted: boolean;
};

function toScatterPoints(sites: RiskDynamicsSite[], highlightedSiteId?: string): ScatterPoint[] {
    return sites.map(site => {
        const quadrant = getQuadrant(site.dependencies, site.impactOnNature);
        return {
            ...site,
            x: site.dependencies,
            y: site.impactOnNature,
            fillClass: getQuadrantFillClass(quadrant),
            strokeClass: getQuadrantStrokeClass(quadrant),
            highlighted: site.id === highlightedSiteId,
        };
    });
}

type DotProps = {
    cx?: number;
    cy?: number;
    fill?: string;
    payload?: ScatterPoint;
    onDotClick?: (site: ScatterPoint, cx: number, cy: number) => void;
    faded?: boolean;
};

const SELECTED_STROKE = 'rgba(61, 61, 61, 1)';
const HOVER_FILTER = 'drop-shadow(0px 1px 2px rgba(33,33,33,0.15))';

function ScatterDot({ cx = 0, cy = 0, payload, onDotClick, faded }: DotProps) {
    const [hovered, setHovered] = useState(false);
    const isSelected = payload?.highlighted ?? false;
    const fillClass = payload?.fillClass ?? 'fill-grey-40';
    const strokeClass = payload?.strokeClass ?? '';

    const handleClick = (e: React.MouseEvent<SVGCircleElement>) => {
        e.stopPropagation();
        if (payload) onDotClick?.(payload, cx, cy);
    };

    const shadow = hovered ? HOVER_FILTER : undefined;
    const hoverHandlers = {
        onMouseEnter: () => setHovered(true),
        onMouseLeave: () => setHovered(false),
    };
    const baseStyle: React.CSSProperties = {
        filter: shadow,
        cursor: 'pointer',
        opacity: faded ? 0.25 : 1,
        transition: 'opacity 0.2s ease',
    };

    if (isSelected) {
        return (
            <circle
                cx={cx}
                cy={cy}
                r={8}
                className={fillClass}
                stroke={SELECTED_STROKE}
                strokeWidth={2}
                style={baseStyle}
                onClick={handleClick}
                {...hoverHandlers}
            />
        );
    }

    return (
        <circle
            cx={cx}
            cy={cy}
            r={4}
            className={`${fillClass} ${strokeClass}`}
            strokeWidth={1.5}
            style={baseStyle}
            onClick={handleClick}
            {...hoverHandlers}
        />
    );
}

type Corner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

const QUADRANT_LABELS: Array<{ quadrant: Quadrant; label: string; corner: Corner; slug: string }> = [
    { quadrant: 'steward-manage', label: 'Steward & manage', corner: 'top-left', slug: 'gnr-quadrant-steward-manage' },
    {
        quadrant: 'priority-integrated-action',
        label: 'Priority for integrated action',
        corner: 'top-right',
        slug: 'gnr-quadrant-priority-integrated-action',
    },
    { quadrant: 'monitor', label: 'Monitor', corner: 'bottom-left', slug: 'gnr-quadrant-monitor' },
    {
        quadrant: 'secure-build-resilience',
        label: 'Secure & build resilience',
        corner: 'bottom-right',
        slug: 'gnr-quadrant-secure-build-resilience',
    },
];

const CORNER_STYLES: Record<string, React.CSSProperties> = {
    'top-left': { position: 'absolute', top: -16, left: 93 },
    'top-right': { position: 'absolute', top: -16, right: 19 },
    'bottom-left': { position: 'absolute', bottom: 21, left: 93 },
    'bottom-right': { position: 'absolute', bottom: 21, right: 19 },
};

type ActiveTooltip = { site: ScatterPoint; cx: number; cy: number };

export default function RiskDynamicsScatterChart({ sites, highlightedSiteId }: Props) {
    const points = useMemo(() => toScatterPoints(sites, highlightedSiteId), [sites, highlightedSiteId]);
    const [activeTooltip, setActiveTooltip] = useState<ActiveTooltip | null>(null);

    const activeTooltipRef = useRef(activeTooltip);
    activeTooltipRef.current = activeTooltip;

    const closeTooltip = useCallback(() => setActiveTooltip(null), []);

    const handleDotClick = useCallback((site: ScatterPoint, cx: number, cy: number) => {
        setActiveTooltip(prev => (prev?.site.id === site.id ? null : { site, cx, cy }));
    }, []);

    useEffect(() => {
        if (!activeTooltip) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setActiveTooltip(null);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [activeTooltip]);

    const dotShape = useCallback(
        (props: DotProps) => {
            const active = activeTooltipRef.current;
            const faded = active !== null && active.site.id !== props.payload?.id;
            return <ScatterDot {...props} onDotClick={handleDotClick} faded={faded} />;
        },
        [handleDotClick]
    );

    return (
        <div style={{ position: 'relative' }} className='mt-4' onClick={closeTooltip}>
            {QUADRANT_LABELS.map(({ quadrant, label, corner, slug }) => (
                <span
                    key={quadrant}
                    className={`flex items-center gap-1 text-xs z-10 ${getQuadrantTextClass(quadrant)}`}
                    style={CORNER_STYLES[corner]}
                >
                    {label}
                    <QuestionButtonHelp type='api' slug={slug} placement='bottom' icon={<Info className='h-3 w-3' />} />
                </span>
            ))}
            <ResponsiveContainer width='100%' height={300}>
                <ScatterChart margin={{ top: 8, right: 8, bottom: 24, left: 24 }}>
                    <XAxis
                        type='number'
                        dataKey='x'
                        domain={[0, 1]}
                        ticks={[0, 0.5, 1]}
                        axisLine={false}
                        tickLine={false}
                        label={{ value: 'Dependencies', position: 'insideBottom', offset: -12, style: { fill: '#666', fontSize: 14 } }}
                    />
                    <YAxis
                        type='number'
                        dataKey='y'
                        domain={[0, 1]}
                        ticks={[0, 0.5, 1]}
                        axisLine={false}
                        tickLine={false}
                        label={{
                            value: 'Impact on Nature',
                            angle: -90,
                            position: 'insideLeft',
                            offset: 12,
                            style: { fill: '#666', fontSize: 14, textAnchor: 'middle' },
                        }}
                    />
                    <ReferenceLine x={0.5} stroke='#ccc' strokeDasharray='4 4' />
                    <ReferenceLine y={0.5} stroke='#ccc' strokeDasharray='4 4' />
                    <Scatter data={points.filter(p => !p.highlighted)} shape={dotShape} isAnimationActive={false} />
                    <Scatter data={points.filter(p => p.highlighted)} shape={dotShape} isAnimationActive={false} />
                </ScatterChart>
            </ResponsiveContainer>

            {activeTooltip && (
                <div
                    onClick={e => e.stopPropagation()}
                    style={{
                        position: 'absolute',
                        left: activeTooltip.cx,
                        top: activeTooltip.cy,
                        transform: 'translate(-50%, calc(-100% - 20px))',
                        zIndex: 20,
                        pointerEvents: 'auto',
                    }}
                >
                    <div style={{ position: 'relative' }}>
                        <ScatterChartTooltip active payload={[{ payload: activeTooltip.site }]} onClose={closeTooltip} />
                        {/* Caret outer — matches the tooltip border colour */}
                        <div
                            style={{
                                position: 'absolute',
                                bottom: -9,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 0,
                                height: 0,
                                borderLeft: '9px solid transparent',
                                borderRight: '9px solid transparent',
                                borderTop: '9px solid transparent',
                            }}
                        />
                        {/* Caret inner — white fill */}
                        <div
                            style={{
                                position: 'absolute',
                                bottom: -7,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 0,
                                height: 0,
                                borderLeft: '8px solid transparent',
                                borderRight: '8px solid transparent',
                                borderTop: '8px solid white',
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
